import React, { useState, useEffect, useRef } from "react";
import { Target, Cpu, Layers, Globe, ArrowRight, ChevronDown, Users, MapPin, Briefcase, BookOpen } from "lucide-react";
import { ABOUT_PILLARS } from "../data/products";
import { Link } from "react-router-dom";

const ICONS = { target: Target, cpu: Cpu, layers: Layers, globe: Globe };

/* ─── Key Metrics ─── */
const METRICS = [
  { value: 8, suffix: "", label: "AI Platforms", icon: Layers },
  { value: 14, suffix: "", label: "Countries", icon: MapPin },
  { value: 7, suffix: "", label: "Industries", icon: Briefcase },
  { value: 24, suffix: "", label: "Sub-segments", icon: BookOpen },
  { value: 9, suffix: "", label: "AI Agents", icon: Cpu },
  { value: 40, suffix: "+", label: "Engagements", icon: Users },
];

/* ─── Platform ecosystem ─── */
const ECOSYSTEM = [
  { name: "Docuweave", role: "Document Intelligence", slug: "grantha" },
  { name: "Luminex", role: "Process Engineering", slug: "vidhana" },
  { name: "D\u1E5B\u1E63\u1E6Di", role: "Control Tower", slug: "drishti" },
  { name: "Sa\u1E43graha", role: "Procurement & Contracts", slug: "samgraha" },
  { name: "Nir\u1E47aya", role: "Vendor Evaluation", slug: "nirnaya" },
  { name: "Assayo", role: "Maturity Assessment", slug: "pariksha" },
  { name: "Formigen", role: "Engineering Design", slug: "rachana" },
];

/* ─── Animated counter ─── */
function useCounter(target, duration = 1800) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            setVal(Math.round((1 - Math.pow(1 - p, 3)) * target));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return [val, ref];
}

function MetricCard({ value, suffix, label, icon: Icon, delay }) {
  const [count, ref] = useCounter(value);
  return (
    <div ref={ref} className="ab-metric" style={{ animationDelay: `${delay}ms` }} data-testid={`ab-metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="ab-metric-icon"><Icon size={20} /></div>
      <div className="ab-metric-val">{count}{suffix}</div>
      <div className="ab-metric-label">{label}</div>
    </div>
  );
}

/* ─── Cognara Multi-Chat Demo with Typewriter ─── */
const CHAT_MESSAGES = [
  { type: "query", text: "Why did the critical path shift last week?" },
  { type: "response", text: "The critical path shifted because vendor delay on valve shipment (PO-2847, expected 4 days ago) pushed Unit-2 mechanical completion 7 days right.", sources: "Checked: Panorix, ProcureX, Docuweave, Evalore", cite: "4 platforms · 12 data points · HIGH" },
  { type: "query", text: "What's the fastest mitigation?" },
  { type: "response", text: "Expedite via air-freight at +₹18 L recovers 5 days. Alternative: re-sequence Unit-3 piping to absorb 3 days at zero cost. Recommend hybrid — air-freight critical valves, re-sequence non-critical.", sources: "Checked: ProcureX, Formigen, Panorix", cite: "3 platforms · 8 data points · HIGH" },
  { type: "query", text: "Draft the expediting letter to the vendor." },
  { type: "response", text: "Letter drafted referencing PO-2847 clause 14.2 (LD applicability), requesting air-freight at vendor cost per FIDIC 8.4. Attached to ProcureX claim file. Ready for CPO sign-off.", sources: "Checked: ProcureX, Docuweave", cite: "2 platforms · 5 data points · HIGH" },
];

function TypewriterText({ text, speed = 18, delay = 0, onDone }) {
  const [displayed, setDisplayed] = useState("");
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  useEffect(() => {
    if (!started) return;
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(interval); onDone && onDone(); }
    }, speed);
    return () => clearInterval(interval);
  }, [started, text, speed, onDone]);
  if (!started) return null;
  return <>{displayed}<span className="sd-cursor">|</span></>;
}

function CognaraDemo() {
  const [visibleCount, setVisibleCount] = useState(0);
  const scrollRef = useRef(null);

  // Show messages one by one
  useEffect(() => {
    if (visibleCount >= CHAT_MESSAGES.length) {
      // Reset after full cycle
      const t = setTimeout(() => setVisibleCount(0), 6000);
      return () => clearTimeout(t);
    }
    const msg = CHAT_MESSAGES[visibleCount];
    const baseDelay = visibleCount === 0 ? 800 : 400;
    const typeTime = msg.text.length * 18 + 600;
    const t = setTimeout(() => setVisibleCount(v => v + 1), baseDelay + typeTime);
    return () => clearTimeout(t);
  }, [visibleCount]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visibleCount]);

  const baseDelay = (idx) => {
    let d = 800;
    for (let i = 0; i < idx; i++) {
      d += CHAT_MESSAGES[i].text.length * 18 + 1000;
    }
    return d;
  };

  return (
    <div className="sd-chat" ref={scrollRef} data-testid="ab-sutradhar-demo">
      <div className="sd-chat-header mono">
        <span className="sd-dot" />{" Cognara \u00b7 AI Console"}
      </div>
      <div className="sd-messages">
        {CHAT_MESSAGES.slice(0, Math.max(visibleCount, 1)).map((msg, i) => {
          const isVisible = i < visibleCount || i === 0;
          if (!isVisible && i > 0) return null;
          const isQuery = msg.type === "query";
          return (
            <div key={`${i}-${visibleCount > CHAT_MESSAGES.length ? 'r' : ''}`} className={`sd-msg ${isQuery ? "sd-msg-query" : "sd-msg-response"}`}>
              <div className="sd-msg-head mono">
                {isQuery ? <><span className="sd-icon-q">&#9671;</span> You</> : <><span className="sd-icon-r">&#9889;</span> Cognara</>}
              </div>
              <div className="sd-msg-body">
                {i < visibleCount - 1 ? msg.text : <TypewriterText text={msg.text} delay={i === 0 && visibleCount === 0 ? 600 : 200} />}
              </div>
              {!isQuery && msg.sources && i < visibleCount && (
                <div className="sd-msg-sources mono">{msg.sources}</div>
              )}
              {!isQuery && msg.cite && i < visibleCount && (
                <div className="sd-msg-cite mono">Sources: {msg.cite}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Expandable Pillar Card ─── */
function PillarCard({ pillar, index }) {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[pillar.icon];
  return (
    <div className={`ab-pillar${open ? " open" : ""}`} onClick={() => setOpen(!open)} data-testid={`about-pillar-${index}`}>
      <div className="ab-pillar-head">
        <div className="ab-pillar-icon"><Icon size={24} /></div>
        <div className="ab-pillar-title">{pillar.title}</div>
        <ChevronDown size={18} className={`ab-pillar-chev${open ? " rotated" : ""}`} />
      </div>
      <div className={`ab-pillar-body${open ? " show" : ""}`}>
        <p>{pillar.desc}</p>
      </div>
    </div>
  );
}

/* ─── Founders section intentionally removed ─── */

export default function About() {
  return (
    <>
      {/* Hero */}
      <section className="pagehead">
        <div className="container">
          <div className="pagehead-inner">
            <div>
              <div className="section-label"><span className="dot" />About Capexara AI</div>
              <h1 className="pagehead-title display" data-testid="about-h1">
                Consulting IP, <em>cognition</em>, and enterprise platforms &mdash; one cognitive AI stack for capital projects.
              </h1>
            </div>
            <p className="pagehead-lede">
              Capexara AI is where the discipline of capital-project consulting meets the reach of generative AI.
              Our thesis: capital projects are humanity&rsquo;s largest act of coordination &mdash; and until now,
              nothing in the technology stack could hold that coordination in its memory.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="ab-metrics-section" data-testid="ab-metrics">
        <div className="container">
          <div className="ab-metrics-grid">
            {METRICS.map((m, i) => (
              <MetricCard key={m.label} {...m} delay={i * 100} />
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach + Ecosystem Visual */}
      <section className="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-copy">
              <div className="section-label"><span className="dot" />Our approach</div>
              <h2 className="display">Strategy &middot; Cognition &middot; Platforms &mdash; one continuous loop.</h2>
              <p>
                We codify decades of BPM, maturity models and platform evaluations into a consulting layer.
                That layer feeds a multi-agent AI cognition engine, which in turn powers our enterprise digital
                platforms. The platforms generate new operating data, which gets refined back into the consulting
                layer. Every engagement leaves the stack smarter.
              </p>
              <div className="ab-pillars" data-testid="about-pillars">
                {ABOUT_PILLARS.map((p, i) => (
                  <PillarCard key={i} pillar={p} index={i} />
                ))}
              </div>
            </div>
            <div className="about-visual">
              <CognaraDemo />
            </div>
          </div>
        </div>
      </section>

      {/* Founders section removed */}

      {/* CTA */}
      <section className="ab-cta-section">
        <div className="container" style={{ textAlign: "center" }}>
          <h2 className="display" style={{ fontSize: "clamp(28px, 3.6vw, 48px)", fontWeight: 500, letterSpacing: "-0.02em", margin: "0 0 16px" }}>
            Ready to see the stack in action?
          </h2>
          <p style={{ color: "var(--text-2)", maxWidth: 520, margin: "0 auto 32px", lineHeight: 1.6 }}>
            Explore our eight platforms or book a 30-minute walkthrough with our team.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/platforms" className="btn btn-primary" data-testid="ab-cta-platforms">
              Explore platforms <ArrowRight size={16} style={{ marginLeft: 6 }} />
            </Link>
            <Link to="/contact" className="btn btn-outline" data-testid="ab-cta-contact">
              Book a walkthrough
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
